import { useCallback, useState } from "react";
import { message } from "antd";
import { convertPermissionsToTree } from "../config/panelTreeConfig";
import {
  getAllOperations,
  Operation,
  getRolesByOperationCode,
  getAdminsByOperationCode,
  getRolesByOperationId,
  getAdminsByOperationId,
} from "../services/operationService";
import { PermissionTransferItem } from "../../assignment/config/types";

export interface PermissionRole {
  id: string;
  name: string;
  code: string;
  description?: string;
}

export interface PermissionAdmin {
  id: string;
  account: string;
  admin_name?: string;
  is_super_admin?: boolean;
  status?: number;
}

interface PermissionNodeInfo {
  service?: string;
  page?: string;
  type?: string;
  code?: string;
}

const findPermissionNode = (
  permissions: PermissionTransferItem[],
  targetKey: string
): PermissionNodeInfo | null => {
  for (const permission of permissions) {
    if (permission.id === targetKey) {
      if (permission.type === "MODULE") {
        if (
          permission.id.startsWith("service_") &&
          permission.permissionData?.serviceName
        ) {
          return {
            service: permission.permissionData.serviceName,
            type: "service",
            code: permission.permissionData.serviceName,
          };
        }
        if (
          permission.id.startsWith("page_") &&
          permission.permissionData?.serviceName &&
          permission.permissionData?.name
        ) {
          return {
            service: permission.permissionData.serviceName,
            page: permission.permissionData.name,
            type: "page",
            code: permission.permissionData.code,
          };
        }
      } else if (permission.permissionData) {
        return {
          service: permission.permissionData.serviceName,
          page: permission.permissionData.page,
          type: "permission",
          code: permission.permissionData.code,
        };
      }
    }
    if (permission.children) {
      const found = findPermissionNode(permission.children, targetKey);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const findPermissionDetail = (
  permissions: PermissionTransferItem[],
  targetId: string
): Operation | null => {
  for (const permission of permissions) {
    if (permission.id === targetId && permission.type === "FUNCTION") {
      return permission.permissionData as Operation;
    }
    if (permission.children) {
      const result = findPermissionDetail(permission.children, targetId);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const usePermissionTree = () => {
  const [permissionTree, setPermissionTree] = useState<PermissionTransferItem[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
  const [rolesWithPermission, setRolesWithPermission] = useState<PermissionRole[]>([]);
  const [adminsWithPermission, setAdminsWithPermission] = useState<PermissionAdmin[]>([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedNodeCode, setSelectedNodeCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);

  const loadRolesWithPermission = useCallback(async (permissionId: string) => {
    setRolesLoading(true);
    try {
      // 先尝试通过 permissionId 查找对应的操作
      const permission = findPermissionDetail(permissionTree, permissionId);
      if (!permission) {
        setRolesWithPermission([]);
        return;
      }

      // 优先使用操作代码查询，如果没有代码则使用ID
      let rolesData: PermissionRole[] = [];
      if (permission.code) {
        rolesData = await getRolesByOperationCode(permission.code);
      } else if (permission.id) {
        rolesData = await getRolesByOperationId(permission.id);
      }

      // 转换数据格式
      const formattedRoles: PermissionRole[] = rolesData.map((role: any) => ({
        id: role.id?.toString() || role.roleId?.toString() || "",
        name: role.name || role.roleName || "",
        code: role.code || role.roleCode || "",
        description: role.description || "",
      }));

      setRolesWithPermission(formattedRoles);
    } catch (error) {
      console.error("加载角色信息失败:", error);
      setRolesWithPermission([]);
    } finally {
      setRolesLoading(false);
    }
  }, [permissionTree]);

  const loadAdminsWithPermission = useCallback(async (permissionId: string) => {
    setAdminsLoading(true);
    try {
      // 先尝试通过 permissionId 查找对应的操作
      const permission = findPermissionDetail(permissionTree, permissionId);
      if (!permission) {
        setAdminsWithPermission([]);
        return;
      }

      // 优先使用操作代码查询，如果没有代码则使用ID
      let adminsData: PermissionAdmin[] = [];
      if (permission.code) {
        adminsData = await getAdminsByOperationCode(permission.code);
      } else if (permission.id) {
        adminsData = await getAdminsByOperationId(permission.id);
      }

      // 转换数据格式
      const formattedAdmins: PermissionAdmin[] = adminsData.map((admin: any) => ({
        id: admin.id?.toString() || admin.adminId?.toString() || "",
        account: admin.account || admin.adminAccount || "",
        admin_name: admin.admin_name || admin.adminName || admin.name || "",
        is_super_admin: admin.is_super_admin ?? admin.isSuperAdmin ?? false,
        status: admin.status ?? 1,
      }));

      setAdminsWithPermission(formattedAdmins);
    } catch (error) {
      console.error("加载管理员信息失败:", error);
      setAdminsWithPermission([]);
    } finally {
      setAdminsLoading(false);
    }
  }, [permissionTree]);

  const loadPermissionDetails = useCallback(
    async (permissionId: string) => {
      setPermissionLoading(true);
      try {
        const permission = findPermissionDetail(permissionTree, permissionId);

        if (permission) {
          setSelectedOperation(permission);
          setSelectedNodeCode(permission.code || "");
          setSelectedService(permission.serviceName || permission.service || "");
          setSelectedPage(permission.page || "");
          await Promise.all([
            loadRolesWithPermission(permissionId),
            loadAdminsWithPermission(permissionId),
          ]);
        } else {
          console.warn("未找到权限，权限ID:", permissionId);
        }
      } catch (error) {
        console.error("加载权限详情失败:", error);
        message.error("加载权限详情失败");
      } finally {
        setPermissionLoading(false);
      }
    },
    [permissionTree, loadRolesWithPermission, loadAdminsWithPermission]
  );

  const loadPermissionTree = useCallback(async () => {
    setLoading(true);
    try {
      const permissions = await getAllOperations();
      const selectedId = selectedOperation?.id;
      const treeData = convertPermissionsToTree(permissions, undefined, selectedId);
      setPermissionTree(treeData);
    } catch (error) {
      console.error("加载权限树失败:", error);
      setPermissionTree([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOperation]);

  const handlePermissionSelection = useCallback(
    async (selectedPermissions: PermissionTransferItem[]) => {
      if (selectedPermissions.length === 0) {
        setSelectedOperation(null);
        setRolesWithPermission([]);
        setAdminsWithPermission([]);
        setSelectedNodeCode("");
        return;
      }

      const permissionId = selectedPermissions[0].id;
      await loadPermissionDetails(permissionId);

      const permissionData = selectedPermissions[0]?.permissionData;
      if (permissionData) {
        setSelectedService(permissionData.serviceName || "");
        setSelectedPage(permissionData.page || "");
        setSelectedNodeCode(permissionData.code || "");
      }
    },
    [loadPermissionDetails]
  );

  const handleTreeNodeSelect = useCallback(
    (selectedKeys: string[]) => {
      if (selectedKeys.length === 0) {
        setSelectedService("");
        setSelectedPage("");
        setSelectedNodeCode("");
        setSelectedOperation(null);
        setRolesWithPermission([]);
        setAdminsWithPermission([]);
        return;
      }

      const selectedKey = selectedKeys[0];
      const nodeInfo = findPermissionNode(permissionTree, selectedKey);

      if (nodeInfo) {
        if (nodeInfo.type === "service") {
          setSelectedService(nodeInfo.service || "");
          setSelectedPage("");
          setSelectedOperation(null);
          setRolesWithPermission([]);
          setAdminsWithPermission([]);
          setSelectedNodeCode(nodeInfo.code || "");
        } else if (nodeInfo.type === "page") {
          setSelectedService(nodeInfo.service || "");
          setSelectedPage(nodeInfo.page || "");
          setSelectedOperation(null);
          setRolesWithPermission([]);
          setAdminsWithPermission([]);
          setSelectedNodeCode(nodeInfo.code || "");
        } else if (nodeInfo.type === "permission") {
          setSelectedService(nodeInfo.service || "");
          setSelectedPage(nodeInfo.page || "");
          setSelectedNodeCode(nodeInfo.code || "");
        }
      }
    },
    [permissionTree]
  );

  const clearSelection = useCallback(() => {
    setSelectedOperation(null);
    setRolesWithPermission([]);
    setAdminsWithPermission([]);
    setSelectedNodeCode("");
  }, []);

  return {
    permissionTree,
    selectedOperation,
    rolesWithPermission,
    adminsWithPermission,
    selectedService,
    selectedPage,
    selectedNodeCode,
    loading,
    permissionLoading,
    rolesLoading,
    adminsLoading,
    loadPermissionTree,
    handlePermissionSelection,
    handleTreeNodeSelect,
    reloadPermissionDetails: loadPermissionDetails,
    clearSelection,
  };
};

